import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import {
    Card,
    Typography,
    Rate,
    Tag,
    Divider,
    Space,
    Spin,
    Alert,
    Empty,
} from "antd";
import {
    UserOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CommentOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

export default function ViewOneExit({ employeeId }) {
    const { authData, backUrl } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [myInterview, setMyInterview] = useState(null);
    const [error, setError] = useState(null);

    const fetchInterviews = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/payroll/exit-interview/employee_answers/?employee_id=${employeeId}`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            const results = res.data;
            setMyInterview(res.data);
            setError(null);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                setMyInterview(null);
                setError(null);
            } else {
                setError("Failed to load exit interview. Please try again.");
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInterviews();
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" tip="Loading exit interview..." />
            </div>
        );
    }

    if (error) {
        return <Alert message="Error" description={error} type="error" showIcon />;
    }

    if (!myInterview) {
        return <Empty description="No exit interview found for this employee." />;
    }

    const item = myInterview;

    return (
        <div style={{ padding: "24px", maxWidth: "1000px", margin: "0 auto" }}>
            <Card title={<Title level={4}>{item.employee_name}</Title>} bordered={false}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {/* Basic Info */}
                    <div>
                        <Text strong>
                            <UserOutlined /> Designation:
                        </Text>{" "}
                        <Text>{item.designation}</Text>
                    </div>

                    {item.submitted_at ? (
                        <div>
                            <Text strong>
                                <CalendarOutlined /> Submitted On:
                            </Text>{" "}
                            <Text>{new Date(item.submitted_at).toLocaleString()}</Text>
                        </div>
                    ) : (
                        <div>
                            <Text strong>
                                <CalendarOutlined /> Status:
                            </Text>{" "}
                            <Tag color="orange">Not Yet Submitted</Tag>
                        </div>
                    )}

                    <Divider />

                    {/* Resignation Reasons */}
                    {item.resignation_reasons?.length > 0 ? (
                        <div>
                            <Text strong>Reasons for Resignation:</Text>
                            <div style={{ marginTop: 8 }}>
                                <Space size={[0, 8]} wrap>
                                    {item.resignation_reasons.map((reason) => (
                                        <Tag color="volcano" key={reason}>
                                            {reason}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        </div>
                    ) : (
                        <Text type="secondary">No reasons provided yet.</Text>
                    )}

                    {/* Specific Event */}
                    {item.specific_event_triggered && item.specific_event_details && (
                        <div>
                            <Text strong>Specific Event Triggered Resignation:</Text>
                            <Paragraph style={{ marginTop: 8 }}>
                                {item.specific_event_details}
                            </Paragraph>
                        </div>
                    )}

                    {/* Decision Timeline */}
                    {item.decision_timeline && (
                        <div>
                            <Text strong>
                                <ClockCircleOutlined /> Decision Timeline:
                            </Text>
                            <Tag color="blue">{item.decision_timeline.replace(/_/g, " ")}</Tag>
                        </div>
                    )}

                    {/* Discussed with Supervisor */}
                    <div>
                        <Text strong>Discussed with Supervisor: </Text>
                        <Tag color={item.discussed_with_supervisor ? "green" : "red"}>
                            {item.discussed_with_supervisor ? "Yes" : "No"}
                        </Tag>
                    </div>

                    <Divider />

                    {/* Ratings */}
                    <Title level={5}>Experience Ratings (out of 5)</Title>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <div>
                            <Text>Training Quality: </Text>
                            <Rate disabled allowHalf value={item.training_rating} />
                            {item.training_rating && <Text type="secondary"> ({item.training_rating})</Text>}
                        </div>
                        <div>
                            <Text>Relationship with Superior: </Text>
                            <Rate disabled value={item.superior_relationship_rating} />
                        </div>
                        <div>
                            <Text>Relationship with Colleagues: </Text>
                            <Rate disabled value={item.colleague_relationship_rating} />
                        </div>
                        <div>
                            <Text>Salary Satisfaction: </Text>
                            <Rate disabled value={item.salary_rating} />
                        </div>
                        <div>
                            <Text>Benefits: </Text>
                            <Rate disabled value={item.benefits_rating} />
                        </div>
                        <div>
                            <Text>Workload Balance: </Text>
                            <Rate disabled value={item.workload_rating} />
                        </div>
                        <div>
                            <Text>Overall Satisfaction: </Text>
                            <Rate disabled value={item.satisfaction_rating} />
                        </div>
                    </Space>

                    {item.benefits_comments && (
                        <div style={{ marginTop: 16 }}>
                            <Text strong>Comments on Benefits:</Text>
                            <Paragraph type="secondary">{item.benefits_comments}</Paragraph>
                        </div>
                    )}

                    <Divider />

                    {item.most_enjoyed && (
                        <div>
                            <Text strong>Most Enjoyed:</Text>
                            <Paragraph>{item.most_enjoyed}</Paragraph>
                        </div>
                    )}

                    {item.least_enjoyed && (
                        <div>
                            <Text strong>Least Enjoyed:</Text>
                            <Paragraph>{item.least_enjoyed}</Paragraph>
                        </div>
                    )}

                    {item.encountered_problems && item.problems_comment && (
                        <div>
                            <Text strong>Significant Problems Encountered:</Text>
                            <Paragraph>{item.problems_comment}</Paragraph>
                        </div>
                    )}

                    <Divider />

                    {/* Recommendations */}
                    <div>
                        <Text strong>Would Recommend Employer: </Text>
                        <Tag color={item.recommend_employer ? "green" : "red"}>
                            <CheckCircleOutlined /> {item.recommend_employer ? "Yes" : "No"}
                        </Tag>
                    </div>
                    {item.recommend_comment && (
                        <Paragraph type="secondary">
                            <CommentOutlined /> "{item.recommend_comment}"
                        </Paragraph>
                    )}

                    <div>
                        <Text strong>Would Reapply in Future: </Text>
                        <Tag color={item.reapply_future ? "green" : "red"}>
                            {item.reapply_future ? "Yes" : "No"}
                        </Tag>
                    </div>
                    {item.reapply_comment && (
                        <Paragraph type="secondary">"{item.reapply_comment}"</Paragraph>
                    )}

                    {item.additional_comments && (
                        <div style={{ marginTop: 16 }}>
                            <Text strong>Additional Comments:</Text>
                            <Paragraph italic>{item.additional_comments}</Paragraph>
                        </div>
                    )}
                </Space>
            </Card>
        </div>
    );
}